import type { Benchmarks } from '../creatives/contract'
import type { ConfidenceThresholds } from '../creatives/confidence'
import { DEFAULT_CONFIDENCE_THRESHOLDS } from '../creatives/confidence'
import { UNASSIGNED } from '../creatives/partition'
import type { BucketRow } from './pool'

/**
 * The hypothesis verdict as a proposal: the journal's rule (doc 03 step 6) applied to the
 * bucket the comparison shows, benchmarked against the account average - PRD 0001's
 * accepted first version, which this screen does not get to improve.
 *
 * The screen proposes and nothing confirms: the journal owns verdict state and is deferred
 * by PRD 0001, so there is no accept and no override here, and nothing on this screen
 * states or implies that a verdict was decided.
 */

export type ProposalKey = 'confirmed' | 'refuted'

export interface Proposal {
  key: ProposalKey
  /** The pooled CPA the arithmetic names, at full division precision. */
  cpa: number
  /** The account average the arithmetic names, from the response's benchmarks. */
  benchmark: number
  conversions: number
  /** The band is provisional: the journal's provisional-sample note rides the basis. */
  provisional: boolean
}

/**
 * Why the cell stays empty. `no-hypothesis` and `partial` are read before everything
 * else; the other two mean the sample is there and the comparison is not. Tests pin the
 * reason, not just the absence.
 */
export type WithheldReason = 'no-hypothesis' | 'partial' | 'no-benchmark' | 'no-cpa'

export type ProposalOutcome = Proposal | { awaiting: number } | { withheld: WithheldReason }

/**
 * Round to four decimals - the precision the contract states for benchmarks and the
 * fixture's `round4` serves them at.
 *
 * The comparison runs at this precision because the client's ratios carry full division
 * precision and a bucket that is the whole account reads 0.666666... against a benchmark
 * of 0.6667, which at full precision would confirm a claim against itself.
 */
export function round4(value: number): number {
  return Math.round(value * 10_000) / 10_000
}

/**
 * The journal's rules, read in this order, the way the contract's `outcomePartial` is
 * read before any verdict rule:
 *
 * 1. The unassigned bucket is not a claim: it holds creatives whose `hypothesisId` is
 *    null, there is nothing to confirm or refute, and no shortfall of evidence that
 *    would create one - so it reads before the floor and before the partial gate
 * 2. An incomplete pooled outcome measure proposes nothing: any of `spend`,
 *    `conversions`, `conversionValue` incomplete, because a bucket with reported
 *    conversions but incomplete spend would rank on a ratio whose cost side is partly
 *    unseen - 100 conversions against the spend that happened to report is not a CPA
 * 3. Below the provisional floor, nothing is proposed: the cell shows the awaiting line
 *    the journal shows, with the shortfall to the floor
 * 4. A null benchmark or a null pooled CPA proposes nothing: with no conversions in scope
 *    the benchmark is null and no arithmetic exists; with no reported spend the CPA is null
 * 5. At or above the floor, with both numbers present, the proposal fires: confirmed when
 *    the pooled CPA rounded to four decimals is below the benchmark, refuted otherwise -
 *    an exact tie reads refuted, because the claim does not beat the average. A human
 *    confirms either way
 */
export function propose(
  bucket: BucketRow,
  benchmarks: Benchmarks,
  thresholds: ConfidenceThresholds = DEFAULT_CONFIDENCE_THRESHOLDS,
): ProposalOutcome {
  if (bucket.id === UNASSIGNED) return { withheld: 'no-hypothesis' }
  if (bucket.outcomeIncomplete) return { withheld: 'partial' }

  const conversions = bucket.metrics.conversions ?? 0
  if (bucket.band === 'insufficient') {
    return { awaiting: thresholds.provisionalAt - conversions }
  }

  if (benchmarks.cpa === null) return { withheld: 'no-benchmark' }
  const cpa = bucket.cpa.value
  if (cpa === null) return { withheld: 'no-cpa' }

  return {
    key: round4(cpa) < round4(benchmarks.cpa) ? 'confirmed' : 'refuted',
    cpa,
    benchmark: benchmarks.cpa,
    conversions,
    provisional: bucket.band === 'provisional',
  }
}